/*
 * Copyright (C) 2018 - 2026 Entgra (Pvt) Ltd, Inc - All Rights Reserved.
 *
 * Unauthorised copying/redistribution of this file, via any medium is strictly prohibited.
 *
 * Licensed under the Entgra Commercial License, Version 1.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *       https://entgra.io/licenses/entgra-commercial/1.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

import { createContext, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { redirectToLogin } from '../api/client';
import { useUserScopes } from '../features/auth/hooks';
import { createStreamPort } from './createStreamPort';
import { setActiveStreamPort } from './logoutSignal';
import { CHANNEL_SCOPES, METER_READINGS_SCOPE, type StreamStatus, type TabPort } from './protocol';

export interface StreamSubscriber {
  eventType: string;
  serials: ReadonlySet<string> | null;
  /** A key on a serial-less channel (e.g. one instant KPI) that this subscriber keeps on the stream. */
  channel?: { name: string; key: string };
  handler: (data: unknown) => void;
}

export interface EventStreamContextValue {
  status: StreamStatus;
  channelStatus: Readonly<Record<string, StreamStatus>>;
  /** Per channel, this tab's keys the stream could not hold. */
  droppedKeys: Readonly<Record<string, readonly string[]>>;
  subscribe: (subscriber: StreamSubscriber) => () => void;
  onReconnected: (callback: () => void) => () => void;
}

const NOOP_UNSUBSCRIBE = () => undefined;

export const EventStreamContext = createContext<EventStreamContextValue>({
  status: 'unavailable',
  channelStatus: {},
  droppedKeys: {},
  subscribe: () => NOOP_UNSUBSCRIBE,
  onReconnected: () => NOOP_UNSUBSCRIBE,
});

function serialOf(data: unknown): string | undefined {
  return data && typeof data === 'object' ? Object.keys(data)[0]?.toUpperCase() : undefined;
}

export interface EventStreamProviderProps {
  children: ReactNode;
  createPort?: () => TabPort;
}

export function EventStreamProvider({ children, createPort = createStreamPort }: EventStreamProviderProps) {
  const scopes = useUserScopes();
  const readingsOk = scopes.data?.includes(METER_READINGS_SCOPE) ?? false;
  // The channels whose stream scope the user holds, as a stable key for the effect below.
  const allowedChannelsKey = Object.entries(CHANNEL_SCOPES)
    .filter(([, scope]) => scopes.data?.includes(scope))
    .map(([channel]) => channel)
    .sort()
    .join(',');
  const scopeOk = readingsOk || allowedChannelsKey !== '';
  const readingsOkRef = useRef(readingsOk);
  readingsOkRef.current = readingsOk;
  const allowedChannels = useRef(new Set<string>());
  allowedChannels.current = new Set(allowedChannelsKey === '' ? [] : allowedChannelsKey.split(','));
  const [status, setStatus] = useState<StreamStatus>('idle');
  const [channelStatus, setChannelStatus] = useState<Record<string, StreamStatus>>({});
  const [droppedKeys, setDroppedKeys] = useState<Record<string, readonly string[]>>({});
  const subscribers = useRef(new Set<StreamSubscriber>());
  const reconnectCallbacks = useRef(new Set<() => void>());
  const port = useRef<TabPort | null>(null);
  const sentSerials = useRef(new Set<string>());
  const sentChannels = useRef(new Map<string, string>());

  const syncSerials = useCallback(() => {
    const target = port.current;
    if (!target) return;
    const wanted = new Set<string>();
    if (readingsOkRef.current) {
      subscribers.current.forEach((subscriber) => subscriber.serials?.forEach((serial) => wanted.add(serial)));
    }
    const added = [...wanted].filter((serial) => !sentSerials.current.has(serial));
    const removed = [...sentSerials.current].filter((serial) => !wanted.has(serial));
    if (added.length > 0) target.postMessage({ type: 'subscribe', serials: added });
    if (removed.length > 0) target.postMessage({ type: 'unsubscribe', serials: removed });
    sentSerials.current = wanted;

    const keysByChannel = new Map<string, Set<string>>();
    subscribers.current.forEach(({ channel }) => {
      if (!channel || !allowedChannels.current.has(channel.name)) return;
      const keys = keysByChannel.get(channel.name) ?? new Set<string>();
      keys.add(channel.key);
      keysByChannel.set(channel.name, keys);
    });
    const wantedChannels = new Map([...keysByChannel].map(([name, keys]) => [name, [...keys].sort().join(',')]));
    wantedChannels.forEach((keys, channel) => {
      if (sentChannels.current.get(channel) !== keys) target.postMessage({ type: 'channel', channel, keys: keys.split(',') });
    });
    sentChannels.current.forEach((_keys, channel) => {
      if (!wantedChannels.has(channel)) target.postMessage({ type: 'channel', channel, keys: [] });
    });
    sentChannels.current = wantedChannels;
  }, []);

  useEffect(() => {
    if (!scopes.isSuccess && !scopes.isError) return undefined;
    if (!scopeOk) {
      setStatus('unavailable');
      setChannelStatus(Object.fromEntries(Object.keys(CHANNEL_SCOPES).map((channel) => [channel, 'unavailable' as const])));
      return undefined;
    }
    const target = createPort();
    port.current = target;
    setActiveStreamPort(target);
    sentSerials.current = new Set();
    sentChannels.current = new Map();
    setDroppedKeys({});
    target.onmessage = ({ data: message }) => {
      switch (message.type) {
        case 'event': {
          const serial = serialOf(message.data);
          subscribers.current.forEach((subscriber) => {
            if (subscriber.eventType === message.eventType
              && (subscriber.serials === null || (serial !== undefined && subscriber.serials.has(serial)))) {
              subscriber.handler(message.data);
            }
          });
          return;
        }
        case 'state':
          setStatus(readingsOkRef.current ? message.status : 'unavailable');
          return;
        case 'channel-state': {
          const channelStatusNow = allowedChannels.current.has(message.channel) ? message.status : 'unavailable';
          setChannelStatus((current) => ({ ...current, [message.channel]: channelStatusNow }));
          return;
        }
        case 'channel-dropped':
          setDroppedKeys((current) => ({ ...current, [message.channel]: message.keys }));
          return;
        case 'reconnected':
          reconnectCallbacks.current.forEach((callback) => callback());
          return;
        case 'login-required':
          redirectToLogin();
          return;
        case 'ping':
          target.postMessage({ type: 'pong' });
          return;
      }
    };
    target.postMessage({ type: 'hello', scopeOk: true });
    syncSerials();
    const onPageHide = () => target.postMessage({ type: 'bye' });
    window.addEventListener('pagehide', onPageHide);
    return () => {
      window.removeEventListener('pagehide', onPageHide);
      target.postMessage({ type: 'bye' });
      target.onmessage = null;
      setActiveStreamPort(null);
      port.current = null;
    };
  }, [scopes.isSuccess, scopes.isError, scopeOk, readingsOk, allowedChannelsKey, createPort, syncSerials]);

  const subscribe = useCallback((subscriber: StreamSubscriber) => {
    subscribers.current.add(subscriber);
    syncSerials();
    return () => {
      subscribers.current.delete(subscriber);
      syncSerials();
    };
  }, [syncSerials]);

  const onReconnected = useCallback((callback: () => void) => {
    reconnectCallbacks.current.add(callback);
    return () => {
      reconnectCallbacks.current.delete(callback);
    };
  }, []);

  const value = useMemo(
    () => ({ status, channelStatus, droppedKeys, subscribe, onReconnected }),
    [status, channelStatus, droppedKeys, subscribe, onReconnected],
  );
  return <EventStreamContext.Provider value={value}>{children}</EventStreamContext.Provider>;
}
